<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Overview/Getting_Started';
    let nextLink= '/Documentation/Overview/Running_Example';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="whats-new-in-freon-20-1"
  bind:intersecting={visibleById["whats-new-in-freon-20-1"]}
  onIntersectChange={(visible) => setVisible("whats-new-in-freon-20-1", visible)}
>🌟 What’s New in Freon 2.0</SectionComponent>
<p>Freon 2.0 is here — and it’s our biggest release yet!<br>
This version brings a completely redesigned <strong>Scoper</strong>, a modernized <strong>web app interface</strong>, and a host of improvements that make developing DSLs smoother, faster, and more fun.</p>
<hr>
<SectionComponent tag="h2" 
  id="major-highlights-2"
  bind:intersecting={visibleById["major-highlights-2"]}
  onIntersectChange={(visible) => setVisible("major-highlights-2", visible)}
>🚀 Major Highlights</SectionComponent>
<h3 id="a-completely-rewritten-scoper-1">✨ A Completely Rewritten Scoper</h3>
<p>The Scoper — responsible for name resolution and namespaces — has been rebuilt from the ground up.<br>
It’s now faster, more reliable, and easier to extend.<br>
You’ll also find new validation for unique names within namespaces.</p>
<h3
  id="a-fresh-web-app-based-on-flowbite-2"
>🎨 A Fresh Web App Based on Flowbite</h3>
<p>The old SMUI interface has been replaced by a new <strong>Flowbite-powered web app</strong>.<br>
Expect a cleaner look, smoother interactions, and full compatibility with the latest version of Svelte 5.</p>
<h3 id="more-freedom-in-naming-3">🔤 More Freedom in Naming</h3>
<p>Model unit names are no longer limited to a narrow set of characters.<br>
Almost any character is now allowed — giving you maximum flexibility in naming your language elements.</p>
<h3 id="smarter-references-4">🔗 Smarter References</h3>
<p>When you rename a node, <strong>all its references update automatically</strong>.<br>
No more manual cleanup — everything stays consistent.</p>
<h3 id="fully-stylable-components-5">🧩 Fully Stylable Components</h3>
<p>Every part of the editor is now <strong>controllable through CSS</strong>.<br>
You can customize the appearance of concepts, fragments, and placeholders for a look that fits your project perfectly.</p>
<h3 id="smarter-dropdowns-6">🔍 Smarter Dropdowns</h3>
<p>Dropdown menus now react to typed input in a <strong>case-insensitive</strong> way — no more missed matches because of capitalization.</p>
<h3 id="multi-line-textboxes-7">📝 Multi-Line Textboxes</h3>
<p>At last, you can create <strong>multi-line text components</strong> directly in the editor.</p>
<h3 id="cleaner-syntax-8">🧾 Cleaner Syntax</h3>
<p>Definition files have been aligned and simplified for a more consistent syntax across all generators.</p>
<h3 id="clearer-external-components-9">🧱 Clearer External Components</h3>
<p>External
components are <strong>no longer experimental</strong> and are now a <strong>fully supported part of the editor framework</strong>,
allowing you to embed custom Svelte components in your projections with confidence.
All boxes previously called <code>External…</code> have been renamed to <code>…ReplacerBox</code> for clarity.</p>
<h3
  id="improved-handling-of-optional-primitives-10"
>⚙️ Improved Handling of Optional Primitives</h3>
<p>Optional primitive properties now behave more intuitively across the parser, serializer, and UI.</p>
<hr>
<SectionComponent tag="h2" 
  id="updated-tooling-3"
  bind:intersecting={visibleById["updated-tooling-3"]}
  onIntersectChange={(visible) => setVisible("updated-tooling-3", visible)}
>🧰 Updated Tooling</SectionComponent>
<p>Freon 2.0 ships with the latest versions of its underlying technologies:</p>
<ul>
<li><strong>Svelte 5</strong> for modern component behavior</li>
<li><strong>AGL 4.2.1-20-RC2</strong> for improved parser generation</li>
<li>Updated dependencies throughout the framework for better performance and compatibility</li>
</ul>
<hr>
<SectionComponent tag="h2" 
  id="bug-fixes-and-polishing-4"
  bind:intersecting={visibleById["bug-fixes-and-polishing-4"]}
  onIntersectChange={(visible) => setVisible("bug-fixes-and-polishing-4", visible)}
>🐛 Bug Fixes and Polishing</SectionComponent>
<p>Dozens of small issues have been resolved — from performance tweaks and validator fixes to undo/redo behavior and layout stability.<br>
Expect a smoother, more predictable editing experience across the board.</p>
<hr>
<SectionComponent tag="h2" 
  id="documentation--developer-experience-5"
  bind:intersecting={visibleById["documentation--developer-experience-5"]}
  onIntersectChange={(visible) => setVisible("documentation--developer-experience-5", visible)}
>📚 Documentation & Developer Experience</SectionComponent>
<p>The developer documentation has been expanded and reorganized, making it easier to dive into Freon’s internals or contribute to the project.<br>
You’ll also notice improved color themes and styling options throughout the UI.</p>
<hr>
<SectionComponent tag="h2" 
  id="new-contributors-6"
  bind:intersecting={visibleById["new-contributors-6"]}
  onIntersectChange={(visible) => setVisible("new-contributors-6", visible)}
>💡 New Contributors</SectionComponent>
<p>We’re thrilled to welcome new contributors to the Freon community!<br>
Special thanks to:</p>
<ul>
<li><strong>@Ulyana-F1re</strong> — for major UI and reference improvements</li>
<li><strong>@globallyunique</strong> — for CSS and editor layout enhancements</li>
</ul>
<hr>
<SectionComponent tag="h2" 
  id="full-changelog-7"
  bind:intersecting={visibleById["full-changelog-7"]}
  onIntersectChange={(visible) => setVisible("full-changelog-7", visible)}
>🔗 Full Changelog</SectionComponent>
<p>If you’d like to explore every commit and pull request,
check the  <a href="https://github.com/freon4dsl/Freon4dsl/releases/tag/v2.0.0" target="_blank" rel="noopener">full changelog on GitHub</a>.</p>


<PrevNextSection {prevLink} {nextLink} />